import React, { useContext, useEffect } from "react";
import { useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { isEmpty } from "lodash";
import {
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Tab,
  Tabs,
} from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";

import Icon from "../../../components/Icon";
import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import AvatarCropper from "../../../components/AvatarCropper";
import ErrorMessage from "../../../components/ErrorMessage";
import VerifiedBadge from "../../../components/VerifiedBadge";
import OperatorIcon from "../../../components/OperatorIcon";
import useUploadFile from "../../../hook/useUploadFile";
import { ThemeToggleButton } from "../../../components/Header";
import { convertDate, defaultPicture } from "../../../utils";
import { convertRoleToPath, convertRoleToText } from "../../../utils/enum";
import {
  changePasswordValidation,
  editUserValidation,
  verifyEmailOTPValidation,
  verifyPhoneNumberOTPValidation,
} from "../../../utils/validation/validation";
import { profileUserContext } from "../../../context/UserContext/ProfileUserContext/ProfileUserContext";
import { areYouSureMessage } from "../../../utils/toastMessage/toastMessage";
import { hostname } from "../../../services/configServices/config";

const ProfileUser = () => {
  const navigate = useNavigate();

  const { pathname } = useLocation();

  const user = useSelector((state) => state.user);
  const auth = useSelector((state) => state.auth);

  const basePath = convertRoleToPath(auth.role);
  const tabs = [
    {
      key: `/${basePath}/profile`,
      title: "ویرایش حساب کاربری",
      content: <EditProfile />,
    },
    {
      key: `/${basePath}/profile/changed-password`,
      title: "ویرایش کلمه عبور",
      content: <ChangePassword />,
    },
    {
      key: `/${basePath}/profile/settings`,
      title: "تنظیمات حساب کاربری",
      content: <ThemeToggleButton isSwitch />,
    },
  ];

  const { handleSendPhoneNumberOTP, handleSendEmailOTP } =
    useContext(profileUserContext);

  const siteTitle = tabs.find((item) => item.key === pathname)?.title;

  return (
    <>
      <SiteTitle title={`${siteTitle}`} />

      <div className="row gy-4">
        <div className="col-lg-8">
          <div className="card h-100">
            <div className="card-body p-24">
              {!isEmpty(user) && (
                <>
                  {!user?.phoneNumberVerified && (
                    <div className="alert alert-danger bg-danger-50 text-danger-500 border-danger-100 py-1 mt-1 text-sm radius-10 d-flex justify-content-between align-items-center">
                      <div className="w-90">
                        شماره موبایل شما تایید نشده است، لطفا نسبت به تایید
                        شماره موبایل خود اقدام نمایید
                      </div>
                      <button
                        onClick={handleSendPhoneNumberOTP}
                        className="border border-success-600 bg-success-50 bg-hover-success-100 text-success-600 text-sm radius-8 text-center py-6 px-20"
                      >
                        تایید
                      </button>
                    </div>
                  )}
                  {!user?.emailVerified && (
                    <div className="alert alert-danger bg-danger-50 text-danger-500 border-danger-100 py-1 mt-1 text-sm radius-10 d-flex justify-content-between align-items-center">
                      <div className="w-90">
                        پست الکترونیک شما تایید نشده است، لطفا نسبت به تایید پست
                        الکترونیک خود اقدام نمایید
                      </div>
                      <button
                        onClick={handleSendEmailOTP}
                        className="border border-success-600 bg-success-50 bg-hover-success-100 text-success-600 text-sm radius-8 text-center py-6 px-20"
                      >
                        تایید
                      </button>
                    </div>
                  )}
                </>
              )}
              <Tabs
                className="nav border-gradient-tab nav-pills border-bottom-0"
                activeKey={pathname}
                onSelect={(k) => navigate(k)}
              >
                {tabs.map((item) => (
                  <Tab
                    key={item.key}
                    tabClassName="border-0"
                    className="pt-20"
                    eventKey={item.key}
                    title={item.title}
                  >
                    {item.content}
                  </Tab>
                ))}
              </Tabs>
            </div>
          </div>
        </div>
        <div className="col-lg-4">
          <UserInfo />
        </div>
      </div>

      <Verified />
    </>
  );
};

export default ProfileUser;

const UserInfo = () => {
  const auth = useSelector((state) => state.auth);
  const user = useSelector((state) => state.user);

  return (
    <div className="user-grid-card position-relative border radius-16 overflow-hidden bg-base h-100">
      <img
        src="/assets/images/user-grid/user-grid-bg9.png"
        alt=""
        className="w-100 object-fit-cover"
      />
      <div className="pb-24 ms-16 mb-24 me-16  mt--100">
        <div className="text-center border border-top-0 border-start-0 border-end-0">
          <img
            src={user?.avatar ? hostname + user?.avatar : defaultPicture}
            alt={`${user?.firstName} ${user?.lastName}`}
            className="border br-white border-width-2-px w-200-px h-200-px rounded-circle object-fit-cover"
          />
          <h6 className="mb-0 mt-16">
            {user?.firstName} {user?.lastName}
          </h6>
          <span className="text-secondary-light mb-16">{user?.email}</span>
        </div>
        <div className="mt-24">
          <h6 className="text-xl mb-16">اطلاعات شما</h6>
          <ul>
            <li className="d-flex align-items-center gap-1 mb-12">
              <span className="w-30 text-md fw-semibold text-primary-light">
                نام
              </span>
              <span className="w-70 text-secondary-light fw-medium">
                : {user?.firstName}
              </span>
            </li>
            <li className="d-flex align-items-center gap-1 mb-12">
              <span className="w-30 text-md fw-semibold text-primary-light">
                نام خانوادگی
              </span>
              <span className="w-70 text-secondary-light fw-medium">
                : {user?.lastName}
              </span>
            </li>
            <li className="d-flex align-items-center gap-1 mb-12">
              <span className="w-30 text-md fw-semibold text-primary-light">
                پست الکترونیک
              </span>
              <span className="w-70 text-secondary-light fw-medium">
                : {user?.email}
              </span>
            </li>
            <li className="d-flex align-items-center gap-1 mb-12">
              <span className="w-30 text-md fw-semibold text-primary-light">
                کد ملی
              </span>
              <span className="w-70 text-secondary-light fw-medium">
                : {user?.nationalNumber}
              </span>
            </li>
            <li className="d-flex align-items-center gap-1 mb-12">
              <span className="w-30 text-md fw-semibold text-primary-light">
                شماره موبایل
              </span>
              <span className="w-70 text-secondary-light fw-medium">
                : {user?.phoneNumber}
              </span>
            </li>
            <li className="d-flex align-items-center gap-1 mb-12">
              <span className="w-30 text-md fw-semibold text-primary-light">
                نقش
              </span>
              <span className="w-70 text-secondary-light fw-medium">
                : {convertRoleToText(auth.role)}
              </span>
            </li>
            <li className="d-flex align-items-center gap-1 mb-12">
              <span className="w-30 text-md fw-semibold text-primary-light">
                آدرس
              </span>
              <span className="w-70 text-secondary-light fw-medium">
                : {user?.address || "تعریف نشده"}
              </span>
            </li>
            <li className="d-flex align-items-center gap-1 mb-12">
              <span className="w-30 text-md fw-semibold text-primary-light">
                تاریخ ثبت نام
              </span>
              <span className="w-70 text-secondary-light fw-medium">
                : {convertDate(user.createdAt, "YYYY/MM/DD - HH:mm:ss")}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

const EditProfile = () => {
  const user = useSelector((state) => state.user);

  const { handleEditUserInfo } = useContext(profileUserContext);

  const { handleUploadFile } = useUploadFile();

  // Validation
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(editUserValidation),
  });

  const handleSetInfo = () => {
    reset({
      avatar: user?.avatar,
      firstName: user?.firstName,
      lastName: user?.lastName,
      phoneNumber: user?.phoneNumber,
      email: user?.email,
      address: user?.address,
    });
  };

  useEffect(() => {
    if (!isEmpty(user)) {
      handleSetInfo();
    }
  }, [user]);

  return (
    <>
      <div className="mb-40">
        <AvatarCropper
          onRemove={() => setValue("avatar", "")}
          value={watch("avatar")}
          onChange={(file) =>
            handleUploadFile(file, (e) => setValue("avatar", e), 1)
          }
        />
      </div>
      <form
        autoComplete="off"
        onSubmit={handleSubmit((d) =>
          areYouSureMessage(
            () => handleEditUserInfo(d),
            "آیا از ویرایش اطلاعات حساب کاربری اطمینان دارید؟",
          ),
        )}
      >
        <div className="row">
          <div className="col-sm-6">
            <div className="mb-20">
              <div>
                <label
                  htmlFor="firstName"
                  className="form-label fw-semibold text-primary-light text-sm mb-8"
                >
                  نام
                </label>
                <input
                  type="text"
                  className="form-control radius-8"
                  id="firstName"
                  placeholder="نام خود را وارد نمایید"
                  {...register("firstName")}
                />
              </div>

              <ErrorMessage message={errors.firstName?.message} />
            </div>
          </div>
          <div className="col-sm-6">
            <div className="mb-20">
              <div>
                <label
                  htmlFor="lastName"
                  className="form-label fw-semibold text-primary-light text-sm mb-8"
                >
                  نام خانوادگی
                </label>
                <input
                  type="text"
                  className="form-control radius-8"
                  id="lastName"
                  placeholder="نام خانوادگی خود را وارد نمایید"
                  {...register("lastName")}
                />
              </div>

              <ErrorMessage message={errors.lastName?.message} />
            </div>
          </div>
          <div className="col-sm-6">
            <div className="mb-20">
              <div>
                <label
                  htmlFor="phoneNumber"
                  className="form-label fw-semibold text-primary-light text-sm mb-8"
                >
                  شماره موبایل{" "}
                  <VerifiedBadge isVerified={user?.phoneNumberVerified} />
                </label>
                <div className="position-relative ">
                  <input
                    type="text"
                    className="form-control radius-8"
                    id="phoneNumber"
                    placeholder="شماره موبایل خود را وارد نمایید"
                    {...register("phoneNumber")}
                  />
                  <OperatorIcon phoneNumber={watch("phoneNumber")} />
                </div>
              </div>

              <ErrorMessage message={errors.phoneNumber?.message} />
            </div>
          </div>
          <div className="col-sm-6">
            <div className="mb-20">
              <div>
                <label
                  htmlFor="email"
                  className="form-label fw-semibold text-primary-light text-sm mb-8"
                >
                  پست الکترونیک{" "}
                  <VerifiedBadge isVerified={user?.emailVerified} />
                </label>
                <input
                  type="text"
                  className="form-control radius-8"
                  id="email"
                  placeholder="پست الکترونیک خود را وارد نمایید"
                  {...register("email")}
                />
              </div>

              <ErrorMessage message={errors.email?.message} />
            </div>
          </div>
          <div className="col-sm-12">
            <div className="mb-20">
              <label
                htmlFor="address"
                className="form-label fw-semibold text-primary-light text-sm mb-8"
              >
                آدرس
              </label>
              <textarea
                name="address"
                className="form-control radius-8"
                id="address"
                rows={5}
                placeholder="آدرس محل سکونت ..."
                {...register("address")}
              />
            </div>
          </div>
        </div>
        <Row className="align-items-center justify-content-between">
          <Col>
            <button
              type="button"
              onClick={handleSetInfo}
              className="border border-danger-600 bg-danger-50 bg-hover-danger-100 text-danger-600 text-md w-100 py-11 radius-8 text-center"
            >
              بازنشانی
            </button>
          </Col>
          <Col>
            <button
              type="submit"
              className="btn btn-primary border border-primary-600 text-md w-100 py-12 radius-8"
            >
              ذخیره
            </button>
          </Col>
        </Row>
      </form>
    </>
  );
};

const ChangePassword = () => {
  const { handleChangePassword } = useContext(profileUserContext);

  // Validation
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(changePasswordValidation),
  });

  return (
    <>
      <form
        autoComplete="off"
        onSubmit={handleSubmit((d) =>
          areYouSureMessage(
            () => handleChangePassword(d),
            "آیا از ویرایش کلمه عبور اطمینان دارید؟",
          ),
        )}
      >
        <div className="row">
          <div className="col-sm-6">
            <div className="mb-20">
              <div>
                <label
                  htmlFor="password"
                  className="form-label fw-semibold text-primary-light text-sm mb-8"
                >
                  کلمه عبور{" "}
                  <span className="badge text-primary-600 bg-primary-100 radius-4">
                    قدیم
                  </span>
                </label>
                <input
                  type="password"
                  className="form-control radius-8"
                  id="password"
                  placeholder="کلمه عبور خود را وارد نمایید"
                  {...register("password")}
                />
              </div>

              <ErrorMessage message={errors.password?.message} />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-6">
            <div className="mb-20">
              <div>
                <label
                  htmlFor="newPassword"
                  className="form-label fw-semibold text-primary-light text-sm mb-8"
                >
                  کلمه عبور{" "}
                  <span className="badge text-primary-600 bg-primary-100 radius-4">
                    جدید
                  </span>
                </label>
                <input
                  type="password"
                  className="form-control radius-8"
                  id="newPassword"
                  placeholder="کلمه عبور خود را وارد نمایید"
                  {...register("newPassword")}
                />
              </div>

              <ErrorMessage message={errors.newPassword?.message} />
            </div>
          </div>
          <div className="col-sm-6">
            <div className="mb-20">
              <div>
                <label
                  htmlFor="confirmPassword"
                  className="form-label fw-semibold text-primary-light text-sm mb-8"
                >
                  تکرار کلمه عبور{" "}
                  <span className="badge text-primary-600 bg-primary-100 radius-4">
                    جدید
                  </span>
                </label>
                <input
                  type="password"
                  className="form-control radius-8"
                  id="confirmPassword"
                  placeholder="تکرار کلمه عبور خود را وارد نمایید"
                  {...register("confirmPassword")}
                />
              </div>

              <ErrorMessage message={errors.confirmPassword?.message} />
            </div>
          </div>
        </div>
        <div className="d-flex align-items-center justify-content-center gap-3">
          <button
            type="button"
            onClick={() => reset()}
            className="border border-danger-600 bg-danger-50 bg-hover-danger-100 text-danger-600 text-md px-56 py-11 radius-8"
          >
            بازنشانی
          </button>
          <button
            type="submit"
            className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
          >
            ذخیره
          </button>
        </div>
      </form>
    </>
  );
};

const Verified = () => {
  const user = useSelector((state) => state.user);

  const {
    otpType,
    setOtpType,
    handleVerifyPhoneNumberOTP,
    handleVerifyEmailOTP,
  } = useContext(profileUserContext);

  const isEmail = otpType === "email";

  // Validation
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(
      isEmail ? verifyEmailOTPValidation : verifyPhoneNumberOTPValidation,
    ),
  });

  useEffect(() => {
    if (!isEmpty(user)) {
      if (isEmail) setValue("email", user?.email);
      else setValue("phoneNumber", user?.phoneNumber);
    }
    setValue("otp", "");
  }, [user, otpType]);

  return (
    <Modal
      show={!!otpType}
      className="modal-dialog-centered"
      size="md"
      onHide={() => setOtpType("")}
    >
      <ModalHeader closeButton>
        <span className="fs-5 fw-bold">
          تایید {isEmail ? "پست الکترونیک" : "شماره موبایل"}
        </span>
      </ModalHeader>
      <ModalBody>
        <form
          autoComplete="off"
          onSubmit={handleSubmit(
            isEmail ? handleVerifyEmailOTP : handleVerifyPhoneNumberOTP,
          )}
        >
          <div className="mb-20">
            <div className="icon-field">
              <span className="icon top-50 translate-middle-y">
                <Icon icon="solar:password-broken" />
              </span>
              <input
                type="text"
                className="form-control h-56-px bg-neutral-50 radius-12 text-center"
                id="otp"
                placeholder="کد تایید ارسال شده را وارد نمایید"
                {...register("otp")}
              />
            </div>

            <ErrorMessage message={errors.otp?.message} />
          </div>

          <p className="text-center">
            زمان باقی مانده:
            <span
              id="otpCountdown"
              className="badge text-primary-600 bg-primary-100 radius-4 mx-6"
            >
              300
            </span>
            ثانیه
          </p>

          <button
            type="submit"
            className="btn btn-primary text-sm btn-sm px-12 py-16 w-100 radius-12"
          >
            تایید {isEmail ? "پست الکترونیک" : "شماره موبایل"}
          </button>
        </form>
      </ModalBody>
    </Modal>
  );
};
